import { duration } from "./format.ts";
import type { ActivityItem, EventPayloads } from "./types.ts";

export interface ActivityLine {
  tone: "ok" | "warn" | "bad" | "accent" | "muted";
  text: string;
}

const clip = (s: string, n = 120) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);
const VIA: Record<string, string> = { discord: "Discord", web: "the dashboard", system: "the system", timeout: "timeout" };

/** One readable line per feed-worthy event; null for noise (working ⇄ waiting flips, tool calls). */
export function describeActivity(item: Pick<ActivityItem, "type" | "payload">): ActivityLine | null {
  switch (item.type) {
    case "state_change": {
      const p = item.payload as EventPayloads["state_change"];
      if (p.from === "created" && p.to === "created") return { tone: "muted", text: "created" };
      if (p.to === "idle" && p.reason === "started") return { tone: "ok", text: "started" };
      if (p.to === "idle" && p.reason === "resumed") return { tone: "ok", text: "resumed" };
      if (p.to === "idle" && p.reason === "runner reconnected") return { tone: "muted", text: "runner reconnected" };
      if (p.to === "stopped") return { tone: "muted", text: `stopped${p.reason ? ` (${p.reason})` : ""}` };
      if (p.to === "errored") return { tone: "bad", text: `errored${p.reason ? `: ${clip(p.reason)}` : ""}` };
      if (p.to === "rate_limited") return { tone: "warn", text: "hit a rate limit" };
      if (p.to === "queued") return { tone: "muted", text: "queued (concurrency cap)" };
      return null;
    }
    case "question": {
      const p = item.payload as EventPayloads["question"];
      return { tone: "warn", text: `asked: “${clip(p.questions[0]?.question ?? "a question")}”` };
    }
    case "permission": {
      const p = item.payload as EventPayloads["permission"];
      const cmd = p.toolName === "Bash" ? ` · ${clip(String((p.input as { command?: string })?.command ?? ""), 60)}` : "";
      return { tone: "warn", text: `asked for permission · ${p.toolName}${cmd}` };
    }
    case "prompt_resolved": {
      const p = item.payload as EventPayloads["prompt_resolved"];
      if (p.summary === "cancelled") return { tone: "muted", text: "prompt cancelled by an interrupt" };
      return { tone: "muted", text: `answered via ${VIA[p.via] ?? p.via}: ${clip(p.summary, 80)}` };
    }
    case "result": {
      const p = item.payload as EventPayloads["result"];
      return p.isError
        ? { tone: "bad", text: `turn ended with an error (${p.subtype})` }
        : { tone: "accent", text: `finished · ${duration(p.durationMs)}${p.text ? ` · ${clip(p.text.split("\n")[0] ?? "", 90)}` : ""}` };
    }
    case "error": {
      const p = item.payload as EventPayloads["error"];
      return { tone: "bad", text: `error${p.category ? ` (${p.category})` : ""}: ${clip(p.message.split("\n")[0] ?? "")}` };
    }
    case "user_prompt": {
      const p = item.payload as EventPayloads["user_prompt"];
      if (p.via === "system") return null;
      const from = p.via === "discord" ? "Discord" : p.via === "app" ? "the Claude app" : "the dashboard";
      return { tone: "muted", text: `prompt from ${from}: ${clip(p.text, 90)}` };
    }
    default:
      return null;
  }
}
